import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtUSD, fmtNum, fmtDate, CHART_COLORS } from "../lib/format";
import { Overline, Monogram, ScoreBar, RiskBadge, Tag, EmptyState } from "../components/shared";
import { useCompare } from "../context/CompareContext";
import { cn } from "../lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip } from "recharts";
import { GitCompareArrows, X, Plus, Loader2 } from "lucide-react";

const RADAR_KEYS = ["Use Case", "Tokenomics", "Team", "Technology", "Legal / Regulatory", "Community", "Marketing", "Decentralization"];

function insider(t) {
  const team = (t.allocations || []).find((a) => a.category === "Team")?.pct || 0;
  const inv = (t.allocations || []).find((a) => a.category === "Investors")?.pct || 0;
  return team + inv;
}

export default function Compare() {
  const { ids, remove, add, clear } = useCompare();
  const [projects, setProjects] = useState([]);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/projects", { params: { sort: "score" } }).then((r) => setAll(r.data.projects)); }, []);

  useEffect(() => {
    if (ids.length === 0) { setProjects([]); return; }
    setLoading(true);
    api.post("/compare", { ids }).then((r) => setProjects(r.data.projects)).finally(() => setLoading(false));
  }, [ids]);

  const compScore = (p, name) => (p.scores?.components || []).find((c) => c.name === name)?.score ?? 0;

  const attrRows = (rows) => (
    <div className="overflow-x-auto bg-card border border-border/60">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            <th className="text-left px-4 py-3 overline text-muted-foreground sticky left-0 bg-muted/50 min-w-[180px]">Attribute</th>
            {projects.map((p) => (
              <th key={p.id} className="text-left px-4 py-3 min-w-[180px]">
                <Link to={`/project/${p.id}`} className="flex items-center gap-2 hover:text-primary">
                  <Monogram symbol={p.token_symbol} name={p.name} size={26} />
                  <span className="font-heading font-bold text-sm">{p.name}</span>
                </Link>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-border/40">
              <td className="px-4 py-2.5 font-medium text-muted-foreground sticky left-0 bg-card">{row.label}</td>
              {projects.map((p) => <td key={p.id} className="px-4 py-2.5">{row.render(p)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const radarData = RADAR_KEYS.map((k) => {
    const row = { category: k.replace(" / Regulatory", "") };
    projects.forEach((p) => { row[p.token_symbol] = compScore(p, k); });
    return row;
  });

  if (ids.length === 0) {
    return (
      <div className="space-y-4 fade-up" data-testid="compare-page">
        <div><Overline>Side-by-side</Overline><h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1">Comparison Engine</h1></div>
        <EmptyState title="No projects selected" sub="Add projects from the Research Explorer or a project page, or pick some below." icon={GitCompareArrows} />
        <div className="bg-card border border-border/60 p-4 max-w-md">
          <div className="overline text-muted-foreground mb-2">Quick add</div>
          <select onChange={(e) => e.target.value && add(e.target.value)} value="" className="w-full h-9 px-2.5 text-sm border border-border rounded-sm" data-testid="compare-quick-add">
            <option value="">Select a project…</option>
            {all.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.token_symbol})</option>)}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 fade-up" data-testid="compare-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><Overline>Side-by-side</Overline><h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1">Comparison Engine</h1></div>
        <div className="flex items-center gap-2">
          <select onChange={(e) => { if (e.target.value) add(e.target.value); e.target.value = ""; }} value="" className="h-9 px-2.5 text-sm border border-border rounded-sm bg-card" data-testid="compare-add">
            <option value="">+ Add project…</option>
            {all.filter((p) => !ids.includes(p.id)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={clear} className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Clear all</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {projects.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-2 bg-card border border-border rounded-sm pl-1 pr-2 py-1">
            <Monogram symbol={p.token_symbol} name={p.name} size={22} /><span className="text-sm font-medium">{p.name}</span>
            <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive"><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : (
        <Tabs defaultValue="Table">
          <TabsList className="flex-wrap justify-start h-auto">
            {["Table", "Scorecard", "Radar", "Tokenomics", "Launch", "Legal", "Technology", "Performance"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`compare-tab-${t.toLowerCase()}`}>{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="Table" className="mt-4">
            {attrRows([
              { label: "Score", render: (p) => <div className="w-24"><ScoreBar score={p.scores?.overall} /></div> },
              { label: "Category", render: (p) => <Tag>{p.category}</Tag> },
              { label: "Sectors", render: (p) => (p.sectors || []).join(", ") },
              { label: "Chains", render: (p) => <span className="num text-xs">{(p.chains || []).join(", ")}</span> },
              { label: "Stage", render: (p) => <Tag>{p.stage}</Tag> },
              { label: "Status", render: (p) => p.status },
              { label: "Jurisdiction", render: (p) => p.jurisdiction },
              { label: "Founding Year", render: (p) => <span className="num">{p.founding_year}</span> },
              { label: "Team Visibility", render: (p) => p.team?.visibility },
              { label: "FDV", render: (p) => <span className="num">{fmtUSD(p.tokenomics?.fdv)}</span> },
              { label: "Funds Raised", render: (p) => <span className="num text-emerald-700">{fmtUSD(p.launch?.funds_raised)}</span> },
              { label: "Red Flags", render: (p) => <RiskBadge level={(p.red_flags || [])[0]?.severity || "None"} count={(p.red_flags || []).length || undefined} /> },
              { label: "Confidence", render: (p) => p.confidence },
            ])}
          </TabsContent>

          <TabsContent value="Scorecard" className="mt-4">
            {attrRows((projects[0]?.scores?.components || []).map((c) => ({
              label: c.name, render: (p) => <div className="w-28"><ScoreBar score={compScore(p, c.name)} /></div>,
            })))}
          </TabsContent>

          <TabsContent value="Radar" className="mt-4">
            <div className="bg-card border border-border/60 p-4 h-[460px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="72%">
                  <PolarGrid stroke="hsl(214 24% 88%)" />
                  <PolarAngleAxis dataKey="category" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
                  {projects.map((p, i) => <Radar key={p.id} name={p.token_symbol} dataKey={p.token_symbol} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.12} strokeWidth={2} />)}
                  <Legend /><Tooltip contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="Tokenomics" className="mt-4">
            {attrRows([
              { label: "Total Supply", render: (p) => <span className="num">{fmtNum(p.tokenomics?.total_supply)}</span> },
              { label: "Circulating @ TGE", render: (p) => <span className="num">{p.tokenomics?.circulating_pct}%</span> },
              { label: "Initial Market Cap", render: (p) => <span className="num">{fmtUSD(p.tokenomics?.initial_mcap)}</span> },
              { label: "FDV", render: (p) => <span className="num">{fmtUSD(p.tokenomics?.fdv)}</span> },
              { label: "Public Price", render: (p) => <span className="num">${p.tokenomics?.public_price}</span> },
              { label: "Team Allocation", render: (p) => <span className="num">{(p.tokenomics?.allocations || []).find((a) => a.category === "Team")?.pct ?? "—"}%</span> },
              { label: "Insider (Team+Investors)", render: (p) => { const v = insider(p.tokenomics || {}); return <span className={cn("num font-semibold", v > 35 ? "text-red-600" : v > 28 ? "text-amber-600" : "text-emerald-600")}>{v}%</span>; } },
              { label: "Utilities", render: (p) => (p.tokenomics?.utilities || []).join(", ") },
            ])}
          </TabsContent>

          <TabsContent value="Launch" className="mt-4">
            {attrRows([
              { label: "Stage", render: (p) => <Tag>{p.stage}</Tag> },
              { label: "TGE Date", render: (p) => <span className="num">{fmtDate(p.launch?.tge_date)}</span> },
              { label: "Hard Cap", render: (p) => <span className="num">{fmtUSD(p.launch?.hard_cap)}</span> },
              { label: "Funds Raised", render: (p) => <span className="num text-emerald-700">{fmtUSD(p.launch?.funds_raised)}</span> },
              { label: "Launchpad", render: (p) => p.launch?.launchpad || "—" },
              { label: "Exchange Partner", render: (p) => p.launch?.exchange_partner || "—" },
              { label: "KYC Required", render: (p) => (p.launch?.kyc_required ? "Yes" : "No") },
              { label: "Sale Phases", render: (p) => <span className="num">{(p.launch?.sale_phases || []).length}</span> },
            ])}
          </TabsContent>

          <TabsContent value="Legal" className="mt-4">
            {attrRows([
              { label: "Legal Entity", render: (p) => p.legal?.entity_name || "—" },
              { label: "Jurisdiction", render: (p) => p.legal?.jurisdiction || "—" },
              { label: "Regulator", render: (p) => p.legal?.regulator || "—" },
              { label: "License", render: (p) => p.legal?.license || "—" },
              { label: "Token Classification", render: (p) => p.legal?.token_classification || "—" },
              { label: "KYC / AML", render: (p) => `${p.legal?.kyc_policy ? "KYC" : "—"} / ${p.legal?.aml_policy ? "AML" : "—"}` },
            ])}
          </TabsContent>

          <TabsContent value="Technology" className="mt-4">
            {attrRows([
              { label: "Blockchain", render: (p) => p.technology?.blockchain },
              { label: "Token Standard", render: (p) => p.technology?.token_standard },
              { label: "Consensus", render: (p) => p.technology?.consensus },
              { label: "Decentralization", render: (p) => p.technology?.decentralization_level },
              { label: "ZK", render: (p) => p.technology?.zk },
              { label: "Upgradeability", render: (p) => p.technology?.upgradeability },
            ])}
          </TabsContent>

          <TabsContent value="Performance" className="mt-4">
            {attrRows([
              { label: "Status", render: (p) => p.status },
              { label: "Current Price", render: (p) => (p.post_launch?.price ? <span className="num">${p.post_launch.price}</span> : <span className="text-muted-foreground text-xs">Not launched</span>) },
              { label: "Listing Price", render: (p) => (p.post_launch?.listing ? <span className="num">${p.post_launch.listing}</span> : "—") },
              { label: "ATH", render: (p) => (p.post_launch?.ath ? <span className="num">${p.post_launch.ath}</span> : "—") },
              { label: "24h Volume", render: (p) => (p.post_launch?.volume ? <span className="num">{fmtUSD(p.post_launch.volume)}</span> : "—") },
              { label: "Market Cap", render: (p) => (p.post_launch?.mcap ? <span className="num">{fmtUSD(p.post_launch.mcap)}</span> : "—") },
              { label: "Holders", render: (p) => (p.post_launch?.holders ? <span className="num">{fmtNum(p.post_launch.holders)}</span> : "—") },
              { label: "Delivery Score", render: (p) => (p.post_launch?.delivery ? <div className="w-24"><ScoreBar score={p.post_launch.delivery} /></div> : "—") },
            ])}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
